/**
 * Calendar 24-hour clock preference + time formatting helpers.
 *
 * The preference hook now lives in `common/hooks/useClock24Hour` (so non-calendar
 * surfaces like the task time picker can share it without an import cycle); it is
 * re-exported here as `useCalendar24Hour` so existing calendar imports are
 * unchanged. Both names read the same localStorage key + change event.
 */
export { useClock24Hour as useCalendar24Hour } from '@/common/hooks/useClock24Hour';

/** Gutter hour label, e.g. "17:00" (24h) or "5 PM" (12h). */
export function formatHourLabel(h: number, use24h: boolean): string {
  if (use24h) {
    const hh = ((h % 24) + 24) % 24;
    return `${String(hh).padStart(2, '0')}:00`;
  }
  if (h === 0 || h === 24) return '12 AM';
  const h12 = h > 12 ? h - 12 : h;
  const suffix = h < 12 ? 'AM' : 'PM';
  return `${h12} ${suffix}`;
}

/** Event time, e.g. "17:30" (24h) or "5:30 PM" / "5 PM" (12h). */
export function formatEventTime(d: Date, use24h: boolean): string {
  const h = d.getHours();
  const m = d.getMinutes();
  if (use24h) {
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  const suffix = h < 12 ? 'AM' : 'PM';
  return m === 0 ? `${h12} ${suffix}` : `${h12}:${String(m).padStart(2, '0')} ${suffix}`;
}
